import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Login.css';
import { GRAPHQL_HTTP_URL } from '../config/api';
import { ADMIN_LOGIN_RETURNING_KEY } from '../constants/adminLoginStorage';
import { refreshTenantBrandingFromAuth } from '../utils/tenantBranding';
import {
  ADMIN_PORTAL_ACCESS_DENIED,
  canAccessAdminPortal,
} from '../utils/adminProfileRouting';

function messageForGatewayTimeout(err, fallback) {
  const status = err?.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to respond. Please try again.';
  }
  return fallback;
}

const emptyDigits = () => ['', '', '', '', '', ''];

export default function LoginVerifyOtp({ onLoginSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const s = location.state || {};
  const otpSessionId = s.otpSessionId || '';
  const codeSentToEmail = s.codeSentToEmail || '';
  const email = (s.email || '').trim();
  const password = s.password || '';

  const [digits, setDigits] = useState(emptyDigits);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRefs = useRef([]);

  const otpCode = digits.join('');

  useEffect(() => {
    setDigits(emptyDigits());
    if (otpSessionId) {
      window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
    }
  }, [otpSessionId]);

  const setDigit = useCallback((index, value) => {
    const d = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = d;
      return next;
    });
    if (d && index < 5) {
      window.requestAnimationFrame(() => inputRefs.current[index + 1]?.focus());
    }
  }, []);

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) return;
      if (index > 0) {
        e.preventDefault();
        inputRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    const chars = text.split('');
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < 6; i += 1) next[i] = chars[i] || '';
      return next;
    });
    const focusIdx = Math.min(chars.length, 5);
    window.requestAnimationFrame(() => inputRefs.current[focusIdx]?.focus());
  };

  const storeAuthData = (data) => {
    localStorage.setItem(ADMIN_LOGIN_RETURNING_KEY, '1');
    const cookieOpts = {
      expires: 1,
      path: '/',
      sameSite: 'strict',
      secure: window.location.protocol === 'https:',
    };
    Cookies.set('accessToken', data.accessToken, cookieOpts);
    if (data.id) Cookies.set('userId', String(data.id), cookieOpts);
    Cookies.set('companyId', data.company.id, cookieOpts);
    Cookies.set('companyName', data.company.companyName, cookieOpts);
    Cookies.set('userName', data.name, cookieOpts);
    localStorage.setItem('userRoles', JSON.stringify(data.roles || []));
    localStorage.setItem('userPermissions', JSON.stringify(data.permissions || []));
  };

  const verifyOtpAndLogin = async () => {
    setError('');
    if (!/^\d{6}$/.test(otpCode)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    const query = `
      mutation VerifyAdminLoginOtp($input: EmployeeOtpVerifyInput!) {
        verifyAdminLoginOtp(input: $input) {
          id
          roles
          permissions
          name
          accessToken
          company {
            id
            companyName
          }
        }
      }
    `;

    setSubmitting(true);
    try {
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: {
              otpSessionId,
              otpCode,
            },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );

      const gqlErrors = response.data?.errors;
      const data = response.data?.data?.verifyAdminLoginOtp;

      if (data?.accessToken) {
        if (!canAccessAdminPortal(data.roles || [])) {
          setError(ADMIN_PORTAL_ACCESS_DENIED);
          return;
        }
        storeAuthData(data);
        // Apply the tenant's white-label branding immediately (no reload needed).
        void refreshTenantBrandingFromAuth();
        if (onLoginSuccess) onLoginSuccess(data);
        navigate('/', { replace: true });
        window.setTimeout(() => {
          window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
        }, 400);
      } else {
        const msg = gqlErrors?.[0]?.message;
        setError(msg || 'Invalid or expired code.');
      }
    } catch (err) {
      setError(messageForGatewayTimeout(err, 'Verification failed. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setDigits(emptyDigits());

    const query = `
      mutation RequestAdminPasswordLoginOtp($input: SignUpUserInput!) {
        requestAdminPasswordLoginOtp(input: $input) {
          otpSessionId
          expiresInSeconds
          resendCooldownSeconds
          codeSentToEmail
        }
      }
    `;
    setSubmitting(true);
    try {
      const response = await axios.post(
        GRAPHQL_HTTP_URL,
        {
          query,
          variables: {
            input: {
              email,
              password,
            },
          },
        },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const gqlErrors = response.data?.errors;
      const data = response.data?.data?.requestAdminPasswordLoginOtp;
      if (data?.otpSessionId) {
        navigate('/login/verify-code', {
          replace: true,
          state: {
            otpSessionId: data.otpSessionId,
            codeSentToEmail: data.codeSentToEmail,
            email,
            password,
          },
        });
      } else {
        const msg = gqlErrors?.[0]?.message;
        setError(msg || 'Could not resend code. Please try again.');
      }
    } catch (err) {
      setError(
        messageForGatewayTimeout(err, 'Failed to resend code. Please try again later.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await verifyOtpAndLogin();
  };

  if (!otpSessionId || !email) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="ld-root">
      <div className="ld-grid">
        <div className="ld-left">
          <div className="ld-hero">
            <img
              src="/getstarted2.png"
              alt="SAS Cleaning Suite"
              className="ld-hero-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const logo = e.currentTarget.parentElement.querySelector('.ld-hero-logo');
                if (logo) logo.style.display = 'block';
              }}
            />
            <img
              src="/logoSas.png?v=5"
              alt="SAS Logo"
              className="ld-hero-logo"
              style={{ display: 'none' }}
            />
          </div>
          <p className="ld-tagline">Trusted by cleaning businesses</p>
          <p className="ld-tagline-sub">
            Manage your operations, schedule, and team all in one place.
          </p>
        </div>

        <div className="ld-right">
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 className="ld-heading">Check your email</h2>
            <p className="ld-subheading">
              Enter the 6-digit code we sent to {codeSentToEmail || email}
            </p>
          </div>

          <form className="ld-form" onSubmit={handleSubmit} noValidate>
            {error && <div className="ld-error">{error}</div>}

            <div className="ld-field">
              <label className="ld-label">Verification code</label>
              <div className="ld-otp-cells" onPaste={handlePaste}>
                {digits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      inputRefs.current[i] = el;
                    }}
                    className="ld-otp-cell"
                    type="text"
                    inputMode="numeric"
                    autoComplete={i === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    disabled={submitting}
                    aria-label={`Digit ${i + 1} of 6`}
                  />
                ))}
              </div>
            </div>

            <button type="submit" className="ld-submit" disabled={submitting}>
              {submitting ? 'Verifying...' : 'Verify & log in'}
            </button>

            <button
              type="button"
              className="ld-link-btn"
              onClick={handleResendOtp}
              disabled={submitting}
            >
              Resend code
            </button>

            <Link to="/login" replace className="ld-link-btn ld-link-btn--block">
              ← Back to sign in
            </Link>
          </form>

          <div className="ld-footer">
            <p>
              By continuing, you agree to our{' '}
              <Link to="/terms-conditions" className="ld-footer-link">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/data-usage-policy" className="ld-footer-link">
                Privacy Policy
              </Link>
            </p>
            <p className="ld-signup-link">
              Don&apos;t have an account?{' '}
              <span onClick={() => window.open('https://sascleaningsuite.com', '_blank')}>
                Sign up
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
